import React, { forwardRef } from 'react';
import { cn } from '../../utils/cn';

export interface GridProps extends React.HTMLAttributes<HTMLDivElement> {
  cols?: 1 | 2 | 3 | 4 | 5 | 6 | 12;
  gap?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  responsive?: {
    sm?: 1 | 2 | 3 | 4 | 5 | 6 | 12;
    md?: 1 | 2 | 3 | 4 | 5 | 6 | 12;
    lg?: 1 | 2 | 3 | 4 | 5 | 6 | 12;
    xl?: 1 | 2 | 3 | 4 | 5 | 6 | 12;
  };
  children?: React.ReactNode;
}

const Grid = forwardRef<HTMLDivElement, GridProps>(
  ({ cols = 1, gap = 'md', responsive, className, children, ...props }, ref) => {
    const gapClasses = {
      none: 'gap-0',
      sm: 'gap-2',
      md: 'gap-4',
      lg: 'gap-6',
      xl: 'gap-8',
    };

    const getColClasses = (cols?: GridProps['cols']) => {
      const colMap: Record<NonNullable<GridProps['cols']>, string> = {
        1: 'grid-cols-1',
        2: 'grid-cols-2',
        3: 'grid-cols-3',
        4: 'grid-cols-4',
        5: 'grid-cols-5',
        6: 'grid-cols-6',
        12: 'grid-cols-12',
      };
      if (!cols) {
        return colMap[1];
      }
      return colMap[cols] ?? colMap[1];
    };

    const responsiveClasses = responsive
      ? Object.entries(responsive)
          .map(([breakpoint, cols]) => {
            const breakpointMap = {
              sm: 'sm:',
              md: 'md:',
              lg: 'lg:',
              xl: 'xl:',
            };
            return `${breakpointMap[breakpoint as keyof typeof breakpointMap]}${getColClasses(cols)}`;
          })
          .join(' ')
      : '';

    return (
      <div
        ref={ref}
        className={cn('grid', getColClasses(cols), gapClasses[gap], responsiveClasses, className)}
        {...props}
      >
        {children}
      </div>
    );
  },
);

Grid.displayName = 'Grid';

export { Grid };
